<template lang="pug">
.grad-reqs-check(:class="gradReqsClass" @click="$emit( 'click', reqsRemaining )")
  p.grad-reqs-check-text {{ displayedText }}
  chevIcon.icon-chevRight
</template>

<script>
import muhsdRequirements from '@/lib/gradRequirements';
import ChevIcon from '@/components/IconChevron';

export default {
  name : 'grad-reqs-check',

  components : {
    ChevIcon,
  },

  data : () => ( {
    gradReqsClass : '',
    reqsRemaining : { gradCredits : [], courses : [], years : [] },
  } ),

  computed : {
    displayedText() {
      return this.isGradRequirementsMet() ? 'You\'re meeting graduation requirements!' : 'Not yet meeting graduation requirements';
    },

    studentPlanSections() {
      return this.$store.getters.studentPlanSections;
    },
  },
  watch : {
    studentPlanSections : {
      immediate : true,
      deep      : true,
      handler() {
        console.log( 'student plan sections changed' );
        this.isGradRequirementsMet();
      },
    },
  },
  methods : {
    isGradRequirementsMet() {
      console.log( 'checking grad reqs' );
      this.reqsRemaining = { gradCredits : [], courses : [], years : [] };
      // Get sections with the courses in each section joined with their actual course object
      const planSections = this.studentPlanSections.map( ( section ) => {
        const r = { ...section };
        if ( !r.courses ) r.courses = [];
        r.courses = r.courses
          .map( courseID => this.$store.getters.getCourseByID( courseID ) )
          .filter( course => !!course );
        return r;
      } );

      /**
			 * First, calculate grad credit requirements.
			 *
			 * Note that the elective requirement is unique since a surplus from other categories counts towards it,
			 * so we have surplus/surplusReqs checks to defer that calculation until the end.
			 */
      let gradCreditsMet = true;
      let surplus = 0;
      const surplusReqs = [];
      muhsdRequirements.gradCredits.forEach( ( gc ) => {
        // Count how many sections contain a course with the current grad credit
        // Thus, see if at least 4 sections satisfy the English requirement, etc.
        const count = planSections.filter( ( section ) => {
          if ( !section.courses ) return false;
          return section.courses.find( course => course.gradCredits.includes( gc.id ) );
        } ).length;
        // DEBUG:
        // if ( count < gc.years ) console.log( 'missing', gc.id, count, gc.years );

        // If this grad credit uses surplus from others (basically just the elective grad credit)
        // delay the calculation until all others have been calculated
        if ( gc.useSurplus ) {
          surplusReqs.push( { req : gc, currCount : count } );
        }
        else {
          const thisGradCreditMet = count >= gc.years;
          gradCreditsMet = gradCreditsMet && thisGradCreditMet;
          this.reqsRemaining.gradCredits.push( { req : gc.id, yearsNeeded : gc.years - count <= 0 ? 0 : gc.years - count } );
        }
        surplus += count - gc.years <= 0 ? 0 : count - gc.years;
      } );
      const electivesReqsMet = surplusReqs.reduce( ( bool, { currCount, req } ) => bool && ( currCount + surplus >= req.years ), true );
      const yearsNeeded = surplusReqs.reduce( ( num, { req, currCount } ) => num + req.years - currCount - surplus, 0 );
      this.reqsRemaining.gradCredits.push( { req : '-Electives', yearsNeeded : yearsNeeded < 0 ? 0 : yearsNeeded } );
      gradCreditsMet = gradCreditsMet && electivesReqsMet;

      /**
			 * Next, calculate course-specific requirements.
			 */
      const { plannedCourses, chosenProgram } = this.$store.getters;
      let coursesMet = true;
      muhsdRequirements.courses.forEach( ( c ) => {
        // Check if a capstone course is planned for the currently chosen program
        const capCourseMet  = !!plannedCourses.find( course => course[c.field] === c.value && course.ctePathways.includes( chosenProgram ) );
        const thisCourseMet = !!plannedCourses.find( course => course[c.field].includes( c.value ) );
        if ( c.type === 'REQ_CAPSTONE' ) {
          coursesMet = coursesMet && capCourseMet;
          this.reqsRemaining.courses.push( { class : c.value, completed : capCourseMet } );
        }
        // Check if a course is planned that satisfies the current requirement
        else {
          coursesMet = coursesMet && thisCourseMet;
          this.reqsRemaining.courses.push( { class : c.value, completed : thisCourseMet } );
        }
      } );

      /**
			 * Last, calculate years requirements (basically, a constraint on the minimum number of courses per year)
			 */
      let yearsMet = true;
      muhsdRequirements.years.forEach( ( y ) => {
        const qualifyingSections = planSections.filter( section => !!section.gradCredits && section.courses.length > 0 && section.year === y.year );
        // console.log( qualifyingSections );
        yearsMet = yearsMet && qualifyingSections.length >= y.courses;
        this.reqsRemaining.years.push( { year : y.year, coursesNeeded : y.courses - qualifyingSections.length <= 0 ? 0 : y.courses - qualifyingSections.length } );
      } );

      // console.log( this.reqsRemaining

      // console.log( `Grad credits: ${gradCreditsMet} | Courses: ${coursesMet} | Years: ${yearsMet}` );

      const gradReqsMet = gradCreditsMet && coursesMet && yearsMet;

      this.gradReqsClass = gradReqsMet ? 'reqs-met' : '';

      return gradReqsMet;
    },

  }

};
</script>

<style lang="scss" scoped>
.grad-reqs-check {
  display: flex;
	font-weight: 500;
	color: #D15756;
  cursor: pointer;
  border: 2px solid #D15756;
  border-radius: 25px;
  align-items: center;
  padding: 5px 10px;
  transition: all 0.2s ease-in-out;
  &-text {
    margin-right: 1rem;
  }

	&.reqs-met {
		color: #58AB99;
    border: 2px solid #58AB99;
    &:hover {
      background-color: #58AB99;
      color: white;
    }
	}
  .icon-chevRight {
    height: 15px;
  }
  &:hover {
    background-color: #D15756;
    color: white;
  }
  &:active {
    transform: scale(0.95);
  }
}
</style>
